import React, { FunctionComponent, useState, useEffect } from 'react';

import { Carousel, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import ReactPlayer from 'react-player';

import { GalleryPreview, OnDeleteFunc } from 'common/gallery/gallery-preview/GalleryPreview';
import { MediaType, getFileFormat } from 'common/upload/Upload';

import 'common/gallery/gallery-modal/GalleryModal.scss';

interface OnHideFunc {
    (): void;
}

export interface Props {
    galleryData: any[];
    title?: string;
    activeIndex: number;
    show: boolean;
    /* defaults to md (undefined) */
    size?: 'sm' | 'lg' | 'xl' | undefined;
    canDelete?: boolean;
    onHide: OnHideFunc;
    onDelete?: OnDeleteFunc;
}

export const GalleryModal: FunctionComponent<Props> = ({
    galleryData,
    title,
    show,
    size,
    activeIndex,
    canDelete,
    onHide,
    onDelete,
}): JSX.Element => {
    const { t } = useTranslation(['common/gallery']);
    const [currentIndex, setCurrentIndex] = useState<number>(activeIndex);
    const [mediaData, setMediaData] = useState(galleryData);

    useEffect(() => {
        setCurrentIndex(activeIndex);
    }, [activeIndex]);

    useEffect(() => {
        setMediaData(galleryData);
    }, [galleryData]);

    const renderItem = (media: any, index: number): JSX.Element => {
        let renderer = null;
        let { url } = media;

        if (!url) {
            url = URL.createObjectURL(media);
        }
        switch (getFileFormat(media.type)) {
            case MediaType.Image:
                renderer = <img src={url} alt={media.name} />;
                break;
            case MediaType.Video:
                renderer = <ReactPlayer height="100%" width="100%" controls url={url} />;
                break;
            case MediaType.Audio:
                renderer = (
                    <audio controls src={url}>
                        <track kind="captions" />
                    </audio>
                );
                break;
            case MediaType.Text:
                renderer = (
                    <>
                        <i className="icon-file-pdf media-icon" />
                    </>
                );
                break;
            default:
                renderer = null;
                break;
        }
        return (
            <Carousel.Item key={index}>
                <div className="carousel-item-container">{renderer}</div>
            </Carousel.Item>
        );
    };

    const handleCarouselSelect = (index: number): void => {
        setCurrentIndex(index);
    };

    return (
        <div className="r4e-gallery-modal">
            <Modal
                aria-labelledby="gallery-modal-title"
                contentClassName="r4e-gallery-modal"
                size={size}
                show={show}
                onHide={onHide}
            >
                <Modal.Header closeButton>
                    <Modal.Title id="gallery-modal-title">{t(title!)}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {mediaData && mediaData[currentIndex] ? (
                        <div className="carousel-file-count">{`${currentIndex + 1} of ${mediaData.length}`}</div>
                    ) : null}
                    <Carousel
                        activeIndex={currentIndex}
                        onSelect={handleCarouselSelect}
                        controls={mediaData && mediaData.length > 1}
                        indicators={false}
                        interval={null}
                    >
                        {mediaData.map((mediaItem: any, mediaKey: number) => {
                            return renderItem(mediaItem, mediaKey);
                        })}
                    </Carousel>
                    {mediaData && mediaData[currentIndex] ? (
                        <div className="carousel-file-name">{mediaData[currentIndex].name}</div>
                    ) : null}
                    <GalleryPreview
                        activeIndex={currentIndex}
                        galleryData={mediaData}
                        wrapContent={false}
                        highlightSelected
                        canDelete={canDelete}
                        canEditImage={false}
                        overflowXContent
                        openModalOnMediaClick
                        displayFileNameOnHover={false}
                        canReOrder={false}
                        onMediaClick={handleCarouselSelect}
                        showVideoControls={false}
                        showAudioControls
                        openModalOnHandleIconClick={false}
                        onDelete={onDelete}
                        scrollSelectedMediaInToView
                    />
                </Modal.Body>
            </Modal>
        </div>
    );
};

GalleryModal.defaultProps = {
    title: 'ATTACHMENTS',
    activeIndex: 0,
};
